import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { compressImage } from "../../utils/compressImage";
import {
  Plus,
  Trash2,
  Save,
  X,
  Car,
  Loader2,
  User,
  FileText,
  DollarSign,
  Wrench,
  Camera,
  Sparkles,
  Star,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronUp,
  Gauge,
  Upload,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export const EMPTY_LISTING_FORM = {
  title: "",
  year: "",
  km: "",
  fuel: "",
  gearbox: "",
  price: "",
  image_url: "",
  status: "disponible",
  highlights: [],
  brand: "",
  model: "",
  trim: "",
  first_registration: "",
  plate: "",
  vin: "",
  exact_km: "",
  engine: "",
  power_din: "",
  fiscal_hp: "",
  displacement: "",
  doors: "",
  seats: "",
  color_exterior: "",
  color_interior: "",
  co2: "",
  crit_air: "",
  ct_valid_until: "",
  ct_result: "",
  keys_count: "",
  service_book: "",
  last_service_date: "",
  last_service_km: "",
  previous_owners: "",
  accident_history: "",
  condition_notes: "",
  tires_condition: "",
  options: [],
  images: [],
  description: "",
  deposit_date: "",
  seller_price: "",
  commission_amount: "",
  commission_percent: "",
  owner_first_name: "",
  owner_last_name: "",
  owner_phone: "",
  owner_email: "",
  owner_address: "",
  technical_notes: "",
};

export const listingToForm = (l = {}) => ({
  ...EMPTY_LISTING_FORM,
  ...l,
  highlights: Array.isArray(l.highlights) ? l.highlights : (l.highlights ? [l.highlights] : []),
  options: Array.isArray(l.options) ? l.options : (l.options ? [l.options] : []),
  images: Array.isArray(l.images) ? l.images : [],
});

export const COMMON_OPTIONS = [
  "Climatisation",
  "Climatisation automatique bi-zone",
  "GPS",
  "Régulateur de vitesse",
  "Limiteur de vitesse",
  "Bluetooth",
  "Apple CarPlay / Android Auto",
  "Sièges chauffants",
  "Sièges cuir",
  "Sièges électriques",
  "Toit ouvrant",
  "Toit panoramique",
  "Caméra de recul",
  "Radar de recul",
  "Radar avant",
  "Vitres électriques",
  "Feux LED / Xénon",
  "Feux automatiques",
  "Essuie-glaces automatiques",
  "Aide au démarrage en côte",
  "Attelage",
  "Jantes alliage",
  "ABS",
  "ESP",
  "Airbags",
  "Volant multifonctions",
  "Volant cuir",
  "Ordinateur de bord",
  "Start & Stop",
  "Direction assistée",
  "Anti-démarrage",
  "Détecteur de pluie",
  "Détecteur d'angle mort",
];

export const buildTitle = (form) =>
  form.title?.trim() || [form.brand, form.model, form.trim].filter(Boolean).join(" ").trim();

export const buildKmLabel = (form) =>
  form.km?.trim() || (form.exact_km ? `${form.exact_km} km` : "");

// ============== FORM UI ==============
export const DepotVenteListingForm = ({ form, setForm, saving, onCancel, onSave, isNew, testIdPrefix = "dv-form", uploadEndpoint }) => {
  const upd = (patch) => setForm({ ...form, ...patch });

  return (
    <div className="bg-[#121212] border border-white/[0.06] rounded-xl p-6" data-testid={`${testIdPrefix}-container`}>
      <div className="flex items-center justify-between mb-6 sticky top-0 bg-[#121212] py-2 z-10 -mx-6 px-6 border-b border-white/[0.04]">
        <h2 className="text-white font-bold text-xl flex items-center gap-2">
          <FileText size={20} className="text-[#FF3B30]" />
          {isNew ? "Nouvelle fiche véhicule" : "Modifier la fiche véhicule"}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={onSave}
            disabled={saving}
            data-testid={`${testIdPrefix}-save-top`}
            className="bg-[#FF3B30] hover:bg-[#D63025] disabled:opacity-50 text-white font-bold px-4 py-2 rounded-md transition-colors uppercase tracking-wider text-xs flex items-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Enregistrement" : "Enregistrer"}
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-2 bg-white/[0.06] hover:bg-white/[0.12] text-white/80 font-medium rounded-md transition-colors text-xs"
          >
            Annuler
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Section 1 — Identity */}
        <Section title="1. Identification du véhicule" icon={<Car size={16} className="text-[#FF3B30]" />} testId={`${testIdPrefix}-section-identity`} defaultOpen>
          <Grid3>
            <Field label="Marque" value={form.brand} onChange={(v) => upd({ brand: v })} placeholder="Peugeot" testId={`${testIdPrefix}-brand`} />
            <Field label="Modèle" value={form.model} onChange={(v) => upd({ model: v })} placeholder="208" testId={`${testIdPrefix}-model`} />
            <Field label="Finition / Version" value={form.trim} onChange={(v) => upd({ trim: v })} placeholder="GT Line" />
          </Grid3>
          <Field label="Titre affiché (auto rempli à partir de marque + modèle + finition si vide)" value={form.title} onChange={(v) => upd({ title: v })} placeholder="Ex : Peugeot 208 GT Line" testId={`${testIdPrefix}-title`} />
          <Grid3>
            <Field label="Année" value={form.year} onChange={(v) => upd({ year: v })} placeholder="2019" testId={`${testIdPrefix}-year`} />
            <Field label="1ère mise en circulation" value={form.first_registration} onChange={(v) => upd({ first_registration: v })} placeholder="12/03/2019" />
            <Field label="Immatriculation" value={form.plate} onChange={(v) => upd({ plate: v })} placeholder="AA-123-AA" testId={`${testIdPrefix}-plate`} />
          </Grid3>
          <Field label="VIN / Numéro de série (17 caractères)" value={form.vin} onChange={(v) => upd({ vin: v })} placeholder="VF3XXXXXXXXXXXXXX" testId={`${testIdPrefix}-vin`} />
        </Section>

        {/* Section 2 — Engine & body */}
        <Section title="2. Caractéristiques techniques" icon={<Wrench size={16} className="text-[#FF3B30]" />} testId={`${testIdPrefix}-section-tech`}>
          <Grid3>
            <SelectField label="Carburant" value={form.fuel} onChange={(v) => upd({ fuel: v })} options={["", "Essence", "Diesel", "Hybride", "Hybride rechargeable", "Électrique", "GPL", "Flexfuel E85"]} testId={`${testIdPrefix}-fuel`} />
            <SelectField label="Boîte de vitesses" value={form.gearbox} onChange={(v) => upd({ gearbox: v })} options={["", "Manuelle 5 vitesses", "Manuelle 6 vitesses", "Automatique", "Semi-automatique (DSG/EDC)", "CVT"]} testId={`${testIdPrefix}-gearbox`} />
            <Field label="Motorisation" value={form.engine} onChange={(v) => upd({ engine: v })} placeholder="1.5 dCi 90 ch" />
          </Grid3>
          <Grid4>
            <Field label="Puissance DIN (ch)" value={form.power_din} onChange={(v) => upd({ power_din: v })} placeholder="130" />
            <Field label="CV fiscaux" value={form.fiscal_hp} onChange={(v) => upd({ fiscal_hp: v })} placeholder="7" />
            <Field label="Cylindrée (cm³)" value={form.displacement} onChange={(v) => upd({ displacement: v })} placeholder="1598" />
            <Field label="CO₂ (g/km)" value={form.co2} onChange={(v) => upd({ co2: v })} placeholder="139" />
          </Grid4>
          <Grid4>
            <Field label="Nombre de portes" value={form.doors} onChange={(v) => upd({ doors: v })} placeholder="5" />
            <Field label="Nombre de places" value={form.seats} onChange={(v) => upd({ seats: v })} placeholder="5" />
            <SelectField label="Vignette Crit'Air" value={form.crit_air} onChange={(v) => upd({ crit_air: v })} options={["", "E - Électrique", "1", "2", "3", "4", "5"]} />
            <Field label="Couleur intérieure" value={form.color_interior} onChange={(v) => upd({ color_interior: v })} placeholder="Noir tissu" />
          </Grid4>
          <Field label="Couleur extérieure" value={form.color_exterior} onChange={(v) => upd({ color_exterior: v })} placeholder="Blanc perle nacré" />
        </Section>

        {/* Section 3 — Condition & documents */}
        <Section title="3. Kilométrage & État" icon={<Gauge size={16} className="text-[#FF3B30]" />} testId={`${testIdPrefix}-section-condition`}>
          <Grid3>
            <Field label="Kilométrage exact" value={form.exact_km} onChange={(v) => upd({ exact_km: v })} placeholder="68 421" testId={`${testIdPrefix}-exact-km`} />
            <Field label="Affichage kilométrage (auto)" value={form.km} onChange={(v) => upd({ km: v })} placeholder="68 000 km" />
            <SelectField label="Propriétaires précédents" value={form.previous_owners} onChange={(v) => upd({ previous_owners: v })} options={["", "1", "2", "3", "4", "5+"]} />
          </Grid3>
          <Grid3>
            <Field label="CT valide jusqu'au" value={form.ct_valid_until} onChange={(v) => upd({ ct_valid_until: v })} placeholder="15/06/2026" />
            <SelectField label="Résultat CT" value={form.ct_result} onChange={(v) => upd({ ct_result: v })} options={["", "Favorable (aucun défaut)", "Défaillances mineures", "Défaillances majeures", "Contre-visite requise", "CT non requis"]} />
            <SelectField label="Nombre de clés" value={form.keys_count} onChange={(v) => upd({ keys_count: v })} options={["", "1", "2", "3"]} />
          </Grid3>
          <Grid3>
            <SelectField label="Carnet d'entretien" value={form.service_book} onChange={(v) => upd({ service_book: v })} options={["", "Complet (concession)", "Complet (multi-garages)", "Partiel", "Absent / perdu"]} />
            <Field label="Dernier entretien — date" value={form.last_service_date} onChange={(v) => upd({ last_service_date: v })} placeholder="10/01/2026" />
            <Field label="Dernier entretien — km" value={form.last_service_km} onChange={(v) => upd({ last_service_km: v })} placeholder="65 000" />
          </Grid3>
          <Grid2>
            <SelectField label="Historique accident / sinistre" value={form.accident_history} onChange={(v) => upd({ accident_history: v })} options={["", "Aucun", "Choc mineur réparé", "Sinistre réparé", "À réparer / non réparé", "Ancien véhicule accidenté (VE)"]} />
            <SelectField label="État général des pneus" value={form.tires_condition} onChange={(v) => upd({ tires_condition: v })} options={["", "Neufs", "Bon état (>4mm)", "Moyens (2-4mm)", "À prévoir (<2mm)"]} />
          </Grid2>
          <TextArea label="Défauts / rayures / points d'attention (interne)" value={form.condition_notes} onChange={(v) => upd({ condition_notes: v })} placeholder="Ex : Petite rayure aile avant droite, tissu banquette AR un peu usé..." />
        </Section>

        {/* Section 4 — Options & highlights */}
        <Section title="4. Équipements & Points forts" icon={<Sparkles size={16} className="text-[#FF3B30]" />} testId={`${testIdPrefix}-section-options`}>
          <TagPicker
            label="Équipements du véhicule"
            values={form.options}
            onChange={(list) => upd({ options: list })}
            suggestions={COMMON_OPTIONS}
            placeholder="Tape une option et Entrée (ou choisis dans la liste)"
            testId={`${testIdPrefix}-options`}
          />
          <TagList
            label="Points forts commerciaux (bullets sur l'annonce publique)"
            values={form.highlights}
            onChange={(list) => upd({ highlights: list })}
            placeholder="Ex : Entretien à jour"
            icon={<Star size={11} className="text-[#FF3B30]" />}
            testId={`${testIdPrefix}-highlights`}
          />
        </Section>

        {/* Section 5 — Photos */}
        <Section title="5. Photos du véhicule" icon={<Camera size={16} className="text-[#FF3B30]" />} testId={`${testIdPrefix}-section-photos`} defaultOpen>
          <MainPhotoField
            value={form.image_url}
            onChange={(v) => upd({ image_url: v })}
            uploadEndpoint={uploadEndpoint}
            testIdPrefix={testIdPrefix}
          />
          <PhotoGalleryEditor
            label="Photos additionnelles (galerie)"
            images={form.images}
            onChange={(list) => upd({ images: list })}
            uploadEndpoint={uploadEndpoint}
            testIdPrefix={testIdPrefix}
          />
        </Section>

        {/* Section 6 — Description publique */}
        <Section title="6. Description publique" icon={<FileText size={16} className="text-[#FF3B30]" />} testId={`${testIdPrefix}-section-description`}>
          <TextArea
            label="Description complète (affichée sur la fiche véhicule publique)"
            value={form.description}
            onChange={(v) => upd({ description: v })}
            placeholder="Rédige une description commerciale — usage précédent, atouts, historique... (10-15 lignes conseillées)"
            rows={6}
            testId={`${testIdPrefix}-description`}
          />
        </Section>

        {/* Section 7 — Commercial */}
        <Section title="7. Commercial & Prix" icon={<DollarSign size={16} className="text-[#FF3B30]" />} testId={`${testIdPrefix}-section-commercial`} defaultOpen>
          <Grid3>
            <Field label="Date de dépôt" value={form.deposit_date} onChange={(v) => upd({ deposit_date: v })} placeholder="15/02/2026" />
            <Field label="Prix vendeur souhaité (net)" value={form.seller_price} onChange={(v) => upd({ seller_price: v })} placeholder="12 000 €" />
            <Field label="Prix de vente affiché (public)" value={form.price} onChange={(v) => upd({ price: v })} placeholder="12 900 €" testId={`${testIdPrefix}-price`} />
          </Grid3>
          <Grid2>
            <Field label="Commission ATR (€)" value={form.commission_amount} onChange={(v) => upd({ commission_amount: v })} placeholder="900" />
            <Field label="Commission ATR (%)" value={form.commission_percent} onChange={(v) => upd({ commission_percent: v })} placeholder="7" />
          </Grid2>
          <div>
            <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Statut</label>
            <select
              data-testid={`${testIdPrefix}-status`}
              value={form.status}
              onChange={(e) => upd({ status: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-[#FF3B30]"
            >
              <option value="disponible">Disponible (visible + bouton Contact)</option>
              <option value="reserve">Réservé (visible + badge jaune)</option>
              <option value="vendu">Vendu (visible avec tampon VENDU)</option>
            </select>
          </div>
        </Section>

        {/* Section 8 — Owner (private) */}
        <Section title="8. Propriétaire (privé — jamais publié)" icon={<User size={16} className="text-[#FF3B30]" />} testId={`${testIdPrefix}-section-owner`} defaultOpen>
          <p className="text-white/40 text-xs -mt-2 mb-1">Infos internes — uniquement toi et l&apos;admin.</p>
          <Grid2>
            <Field label="Prénom" value={form.owner_first_name} onChange={(v) => upd({ owner_first_name: v })} placeholder="Jean" testId={`${testIdPrefix}-owner-firstname`} />
            <Field label="Nom" value={form.owner_last_name} onChange={(v) => upd({ owner_last_name: v })} placeholder="Dupont" testId={`${testIdPrefix}-owner-lastname`} />
          </Grid2>
          <Grid2>
            <Field label="Téléphone" value={form.owner_phone} onChange={(v) => upd({ owner_phone: v })} placeholder="06 12 34 56 78" testId={`${testIdPrefix}-owner-phone`} />
            <Field label="Email" value={form.owner_email} onChange={(v) => upd({ owner_email: v })} placeholder="jean.dupont@email.fr" />
          </Grid2>
          <Field label="Adresse complète" value={form.owner_address} onChange={(v) => upd({ owner_address: v })} placeholder="12 rue Exemple, 63000 Clermont-Ferrand" />
        </Section>

        {/* Section 9 — Internal notes */}
        <Section title="9. Notes techniques internes" icon={<Wrench size={16} className="text-[#FF3B30]" />} testId={`${testIdPrefix}-section-notes`}>
          <TextArea
            label="Notes internes (jamais publiées)"
            value={form.technical_notes}
            onChange={(v) => upd({ technical_notes: v })}
            placeholder="Ex : Prévoir plaquettes avant, embrayage OK, roulement AR gauche à surveiller..."
            rows={4}
          />
        </Section>

        <div className="flex gap-3 pt-4 border-t border-white/[0.06] sticky bottom-0 bg-[#121212] py-4 -mx-6 px-6">
          <button
            onClick={onSave}
            disabled={saving}
            data-testid={`${testIdPrefix}-save`}
            className="flex-1 bg-[#FF3B30] hover:bg-[#D63025] disabled:opacity-50 text-white font-bold py-3 rounded-md transition-colors uppercase tracking-wider text-sm flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? "Enregistrement..." : isNew ? "Créer la fiche véhicule" : "Enregistrer les modifications"}
          </button>
          <button
            onClick={onCancel}
            className="px-6 bg-white/[0.06] hover:bg-white/[0.12] text-white/80 font-medium rounded-md transition-colors text-sm"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== UI PRIMITIVES ==============
const Section = ({ title, icon, children, defaultOpen = false, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-lg overflow-hidden" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
      >
        <span className="flex items-center gap-2 text-white font-semibold text-sm">
          {icon}
          {title}
        </span>
        {open ? <ChevronUp size={16} className="text-white/40" /> : <ChevronDown size={16} className="text-white/40" />}
      </button>
      {open && <div className="p-4 pt-2 space-y-3 border-t border-white/[0.04]">{children}</div>}
    </div>
  );
};

const Grid2 = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
const Grid3 = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">{children}</div>;
const Grid4 = ({ children }) => <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{children}</div>;

const Field = ({ label, value, onChange, placeholder, testId, type = "text" }) => (
  <div>
    <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">{label}</label>
    <input
      type={type}
      value={value || ""}
      data-testid={testId}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-[#FF3B30] text-sm"
      placeholder={placeholder}
    />
  </div>
);

const SelectField = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">{label}</label>
    <select
      value={value || ""}
      data-testid={testId}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-[#FF3B30] text-sm"
    >
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt || "—"}</option>
      ))}
    </select>
  </div>
);

const TextArea = ({ label, value, onChange, placeholder, rows = 3, testId }) => (
  <div>
    <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">{label}</label>
    <textarea
      value={value || ""}
      data-testid={testId}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-[#FF3B30] text-sm resize-y"
      placeholder={placeholder}
    />
  </div>
);

const TagList = ({ label, values, onChange, placeholder, icon, testId }) => {
  const [input, setInput] = useState("");
  const list = Array.isArray(values) ? values : [];
  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (list.includes(v)) { setInput(""); return; }
    onChange([...list, v]);
    setInput("");
  };
  return (
    <div>
      <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          data-testid={testId}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          className="flex-1 bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2 text-white focus:outline-none focus:border-[#FF3B30] text-sm"
          placeholder={placeholder}
        />
        <button type="button" onClick={add} className="px-3 bg-white/[0.06] hover:bg-white/[0.12] text-white/80 rounded-md text-xs">
          <Plus size={13} />
        </button>
      </div>
      {list.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {list.map((v, i) => (
            <span key={`${v}-${i}`} className="inline-flex items-center gap-1.5 text-xs text-white/80 bg-white/[0.06] border border-white/[0.08] px-2.5 py-1 rounded-full">
              {icon}
              {v}
              <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-400">
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const TagPicker = ({ label, values, onChange, suggestions, placeholder, testId }) => {
  const list = Array.isArray(values) ? values : [];
  const [input, setInput] = useState("");
  const toggle = (opt) => {
    if (list.includes(opt)) onChange(list.filter((x) => x !== opt));
    else onChange([...list, opt]);
  };
  const addCustom = () => {
    const v = input.trim();
    if (!v || list.includes(v)) { setInput(""); return; }
    onChange([...list, v]);
    setInput("");
  };
  return (
    <div>
      <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">{label}</label>
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={input}
          data-testid={testId}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
          className="flex-1 bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2 text-white focus:outline-none focus:border-[#FF3B30] text-sm"
          placeholder={placeholder}
        />
        <button type="button" onClick={addCustom} className="px-3 bg-white/[0.06] hover:bg-white/[0.12] text-white/80 rounded-md text-xs">
          <Plus size={13} />
        </button>
      </div>
      {list.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3 pb-3 border-b border-white/[0.04]">
          {list.map((v, i) => (
            <span key={`${v}-${i}`} className="inline-flex items-center gap-1.5 text-xs text-white bg-[#FF3B30]/20 border border-[#FF3B30]/40 px-2.5 py-1 rounded-full">
              {v}
              <button type="button" onClick={() => toggle(v)} className="text-white/60 hover:text-white">
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {suggestions.filter((s) => !list.includes(s)).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => toggle(s)}
            className="text-xs text-white/50 hover:text-white bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.06] px-2 py-0.5 rounded-full transition-colors"
          >
            + {s}
          </button>
        ))}
      </div>
    </div>
  );
};

// ============== IMAGE UPLOAD HELPERS ==============
export const buildImageUrl = (relativeOrAbsoluteUrl) => {
  if (!relativeOrAbsoluteUrl) return "";
  if (/^https?:\/\//i.test(relativeOrAbsoluteUrl)) return relativeOrAbsoluteUrl;
  return `${API}${relativeOrAbsoluteUrl}`;
};

const uploadImageFile = async (file, uploadEndpoint) => {
  if (!uploadEndpoint) {
    toast.error("Endpoint d'upload manquant — contacte le développeur");
    return null;
  }
  if (!file.type.startsWith("image/")) {
    toast.error(`${file.name}: seules les images sont acceptées`);
    return null;
  }
  // Client-side compression: shrinks big phone photos (typically 4-8 MB HEIC/JPG)
  // down to ~400-800 KB. Falls back to the original if compression fails.
  const compressed = await compressImage(file);
  const fd = new FormData();
  fd.append("file", compressed);
  try {
    const res = await axios.post(`${API}${uploadEndpoint}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data?.url || null;
  } catch (err) {
    toast.error(err.response?.data?.detail || `Erreur d'upload : ${file.name}`);
    return null;
  }
};

const MainPhotoField = ({ value, onChange, uploadEndpoint, testIdPrefix }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const url = await uploadImageFile(f, uploadEndpoint);
    setUploading(false);
    if (url) {
      onChange(url);
      toast.success("Photo principale mise à jour");
    }
    e.target.value = "";
  };

  return (
    <div className="space-y-2">
      <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">
        Photo principale (couverture)
      </label>
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-image-upload`}
          className="inline-flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] disabled:opacity-50 text-white font-bold px-4 py-2.5 rounded-md text-xs uppercase tracking-wider whitespace-nowrap"
        >
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {uploading ? "Envoi..." : "Prendre / choisir photo"}
        </button>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="… ou coller une URL d'image"
          data-testid={`${testIdPrefix}-image-url`}
          className="flex-1 bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-[#FF3B30] text-sm"
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*,.heic,.heif"
          className="hidden"
          onChange={handleFileChange}
          data-testid={`${testIdPrefix}-image-file-input`}
        />
      </div>
      {value && (
        <div className="w-full h-40 rounded-md overflow-hidden border border-white/[0.06] bg-[#1a1a1a]">
          <img
            src={buildImageUrl(value)}
            alt="Aperçu principal"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>
      )}
    </div>
  );
};

export const PhotoGalleryEditor = ({ label, images, onChange, uploadEndpoint, testIdPrefix }) => {
  const fileRef = useRef(null);
  const [input, setInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const list = Array.isArray(images) ? images : [];

  const add = () => {
    const v = input.trim();
    if (!v) return;
    onChange([...list, v]);
    setInput("");
  };
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const handleFilesChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const results = await Promise.all(files.map((f) => uploadImageFile(f, uploadEndpoint)));
    const urls = results.filter(Boolean);
    setUploading(false);
    if (urls.length > 0) {
      onChange([...list, ...urls]);
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} ajoutée${urls.length > 1 ? "s" : ""}`);
    }
    e.target.value = "";
  };

  return (
    <div>
      <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">{label}</label>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-gallery-upload`}
          className="inline-flex items-center justify-center gap-2 bg-[#FF3B30]/80 hover:bg-[#FF3B30] disabled:opacity-50 text-white font-bold px-4 py-2 rounded-md text-xs uppercase tracking-wider whitespace-nowrap"
        >
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {uploading ? "Envoi..." : "Ajouter des photos"}
        </button>
        <input
          type="text"
          value={input}
          data-testid={`${testIdPrefix}-image-add`}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          className="flex-1 bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2 text-white focus:outline-none focus:border-[#FF3B30] text-sm"
          placeholder="… ou coller une URL d'image + Entrée"
        />
        <button type="button" onClick={add} className="px-3 bg-white/[0.06] hover:bg-white/[0.12] text-white/80 rounded-md text-xs" title="Ajouter l'URL">
          <Plus size={13} />
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,.heic,.heif"
          multiple
          className="hidden"
          onChange={handleFilesChange}
          data-testid={`${testIdPrefix}-gallery-file-input`}
        />
      </div>
      {list.length === 0 ? (
        <div className="text-white/30 text-xs italic">Aucune photo additionnelle — la photo principale sera utilisée.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {list.map((url, i) => (
            <div key={`${url}-${i}`} className="relative group bg-[#1a1a1a] border border-white/[0.06] rounded-md overflow-hidden aspect-video">
              <img src={buildImageUrl(url)} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
              <div className="absolute top-1 right-1 flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 bg-black/70 hover:bg-black/90 disabled:opacity-30 rounded" title="Monter">
                  <ArrowUp size={12} className="text-white" />
                </button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} className="p-1.5 bg-black/70 hover:bg-black/90 disabled:opacity-30 rounded" title="Descendre">
                  <ArrowDown size={12} className="text-white" />
                </button>
                <button type="button" onClick={() => remove(i)} className="p-1.5 bg-red-500/80 hover:bg-red-500 rounded" title="Supprimer">
                  <Trash2 size={12} className="text-white" />
                </button>
              </div>
              <div className="absolute bottom-1 left-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">#{i + 1}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
